import type { EmailBlockAttributes, EmailBlockSchema, EmailCatalogMap, EmailRenderContext } from './types';
import { FONT_OPTIONS, FONT_WEIGHT_OPTIONS, TEXT_TRANSFORM_OPTIONS } from './types';

/**
 * The email block catalog. Each entry's `render()` emits the SAME inline-styled HTML fragment
 * the legacy `generateEmailTemplate` (utils/email-templates.ts) produced for that section, so
 * the visual builder and the legacy compiler stay visually identical. The seeder
 * (useEmailSeed.ts) overlays the live `formValues` onto these defaults, so a freshly-seeded
 * builder preview matches the current email exactly.
 *
 * NOTE: this mirrors the cart-widget builder catalog pattern, but emails are a FLAT stack —
 * there is no nesting / canHaveChildren. Helpers in `useEmailBlockTree.ts` operate on the flat list.
 */

// Default "your logo here" placeholder shown until a merchant uploads a logo. Mirrors the constant in
// utils/email-templates.ts (kept local to avoid a circular import — email-templates imports this catalog).
const DEFAULT_LOGO_SVG = `<img src="https://st5.depositphotos.com/69915036/62675/v/450/depositphotos_626754468-stock-illustration-your-logo-here-placeholder-symbol.jpg" alt="Your logo here" style="display: block; width: 100%; max-width: 100%; height: auto; object-fit: contain;" />`;

/** Shared inline style block for the three text blocks (heading/paragraph/subtext). */
function textBlockStyle(a: EmailBlockAttributes, opts: { margin: string; textAlign?: string }): string {
  const align = opts.textAlign ? `text-align: ${opts.textAlign}; ` : '';

  return [
    `color: ${a.fontColor};`,
    `font-family: ${a.fontFamily};`,
    `font-size: ${a.fontSize}px;`,
    `font-weight: ${a.fontWeight};`,
    `text-transform: ${a.fontStyle};`,
    `line-height: ${a.lineHeight};`,
    `letter-spacing: ${a.letterSpacing}px;`,
    `${align}margin: ${opts.margin};`,
  ].join(' ');
}

/**
 * Restricts a user-supplied link to safe URL schemes (http(s), mailto, anchors, relative paths),
 * falling back to '#'. Blocks `javascript:`/`data:` hrefs from reaching the preview or sent document.
 */
function safeHref(url: unknown): string {
  const value = String(url ?? '');

  return /^(https?:\/\/|mailto:|#|\/)/i.test(value) ? value : '#';
}

/** Inspector controls shared by heading/paragraph/subtext. */
const textControls = () => [
  { attr: 'text', group: 'Content', kind: 'textarea' as const, label: 'Text' },
  { attr: 'fontColor', group: 'Text', kind: 'color' as const, label: 'Color' },
  { attr: 'fontFamily', group: 'Text', kind: 'font' as const, label: 'Font' },
  { attr: 'fontSize', group: 'Text', kind: 'number' as const, label: 'Size (px)' },
  { attr: 'fontWeight', group: 'Text', kind: 'select' as const, label: 'Weight', options: FONT_WEIGHT_OPTIONS },
  { attr: 'fontStyle', group: 'Text', kind: 'select' as const, label: 'Transform', options: TEXT_TRANSFORM_OPTIONS },
  { attr: 'lineHeight', group: 'Text', kind: 'number' as const, label: 'Line Height' },
  { attr: 'letterSpacing', group: 'Text', kind: 'number' as const, label: 'Letter Spacing (px)' },
];

/** Inspector controls shared by button / store-credit CTA. */
const buttonControls = [
  { attr: 'text', group: 'Content', kind: 'text' as const, label: 'Button Text' },
  { attr: 'link', group: 'Content', kind: 'text' as const, label: 'Button Link' },
  { attr: 'background', group: 'Style', kind: 'color' as const, label: 'Background' },
  { attr: 'fontColor', group: 'Style', kind: 'color' as const, label: 'Text Color' },
  { attr: 'borderRadius', group: 'Style', kind: 'number' as const, label: 'Border Radius (px)' },
  { attr: 'fontStyle', group: 'Style', kind: 'select' as const, label: 'Transform', options: TEXT_TRANSFORM_OPTIONS },
];

export const EMAIL_CATALOG: EmailCatalogMap = {
  brandName: {
    category: 'content',
    controls: [
      { attr: 'text', group: 'Content', kind: 'text', label: 'Brand Name' },
      { attr: 'fontColor', group: 'Text', kind: 'color', label: 'Color' },
      { attr: 'fontFamily', group: 'Text', kind: 'font', label: 'Font' },
      { attr: 'fontSize', group: 'Text', kind: 'number', label: 'Size (px)' },
    ],
    // Empty text falls back to the store name at render time.
    defaultAttributes: {
      fontColor: '#000000',
      fontFamily: "'Tiro Bangla', serif",
      fontSize: 21.95,
      fontWeight: 400,
      text: '',
    },
    displayName: 'Brand Name',
    icon: 'LineFont',
    paletteVisible: true,
    render(a, ctx) {
      const text = a.text != null && String(a.text).length > 0 ? a.text : ctx.storeName;

      return `<span style="font-family: ${a.fontFamily}; font-weight: ${a.fontWeight}; font-size: ${a.fontSize}px; line-height: 22px; color: ${a.fontColor}; text-align: left; display: flex;">${text}</span>`;
    },
    type: 'brandName',
  },

  button: {
    category: 'content',
    controls: [
      ...buttonControls,
      { attr: 'fontFamily', group: 'Style', kind: 'font', label: 'Font' },
      { attr: 'fontWeight', group: 'Style', kind: 'select', label: 'Weight', options: FONT_WEIGHT_OPTIONS },
    ],
    defaultAttributes: {
      background: '#000000',
      borderRadius: 0,
      fontColor: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'none',
      fontWeight: 'regular',
      link: '#',
      text: 'View my claim',
    },
    displayName: 'Button',
    icon: 'LineBox',
    paletteVisible: true,
    render(a) {
      return `
      <div style="text-align: left; margin-bottom: 32px;" width="100%">
        <a href="${safeHref(a.link)}" style="
          display: block;
          background-color: ${a.background};
          border-radius: ${a.borderRadius}px;
          color: ${a.fontColor};
          font-family: ${a.fontFamily};
          font-size: 16px;
          text-transform: ${a.fontStyle};
          font-weight: ${a.fontWeight};
          text-decoration: none;
          text-align: center;
          width: 100%;
          height: 58px;
          line-height: 58px;
          margin: 48px auto;
        ">${a.text}
        </a>
      </div>`;
    },
    type: 'button',
  },

  divider: {
    category: 'layout',
    controls: [
      { attr: 'color', group: 'Style', kind: 'color', label: 'Color' },
      { attr: 'thickness', group: 'Style', kind: 'number', label: 'Thickness (px)' },
      { attr: 'marginY', group: 'Spacing', kind: 'number', label: 'Vertical Margin (px)' },
    ],
    defaultAttributes: { color: '#E1E6EF', marginY: 16, thickness: 1 },
    displayName: 'Divider',
    icon: 'LineEllipsisH',
    paletteVisible: true,
    render(a) {
      return `<hr style="border: none; border-top: ${a.thickness}px solid ${a.color}; margin: ${a.marginY}px 0;" />`;
    },
    type: 'divider',
  },

  footer: {
    category: 'content',
    controls: [
      { attr: 'footerContent', group: 'Content', kind: 'textarea', label: 'Footer Text' },
      { attr: 'togglePoweredByOpEnabled', group: 'Content', kind: 'toggle', label: 'Show "Powered by OP"' },
    ],
    defaultAttributes: { footerContent: 'All Rights Reserved', togglePoweredByOpEnabled: true },
    displayName: 'Footer',
    icon: 'LineInbox',
    paletteVisible: true,
    // NOTE: the year is baked at compile/preview time (no backend [[currentYear]] variable available),
    // so an email designed in December but sent in January shows the design-time year. Matches legacy.
    render(a) {
      const poweredBy = a.togglePoweredByOpEnabled
        ? `
        <div style="margin-bottom: 8px;">
          <a href="#" style="color: #1D2433; text-decoration: underline; margin-right: 9.6px; font-size: 12px;">Terms</a>
          <a href="#" style="color: #1D2433; text-decoration: underline; font-size: 12px;">File a Claim</a>
        </div>
        <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="margin-top: 16px;">
          <tr>
            <td style="color: #A3A9B7; font-size: 12px; vertical-align: middle; padding-right: 8px; background-color: #FFFFFF !important; padding-left: 0 !important;">
              Powered by
            </td>
            <td style="vertical-align: middle; background-color: #FFFFFF !important;">
              <img
                src="https://op-media-asset.s3.us-west-1.amazonaws.com/assets/emails/logo-light.png"
                alt="Order Protection Logo"
                style="width: 150px; height: 32px; max-width: 100%; max-height: 100%; object-fit: contain; vertical-align: middle;"
              />
            </td>
          </tr>
        </table>`
        : '';

      return `
      <div style="text-align: left; margin-top: 32px;">
        <div style="font-size: 12px; color: #1D2433; margin-bottom: 8px;">
          ${new Date().getFullYear()}, ${a.footerContent}
        </div>
        ${poweredBy}
      </div>`;
    },
    singleton: true,
    type: 'footer',
  },

  header: {
    canHaveChildren: true,
    category: 'layout',
    controls: [],
    defaultAttributes: {},
    displayName: 'Header Row',
    icon: 'LineBars',
    layout: 'row',
    paletteVisible: true,
    // Horizontal row that groups the logo + brand name side by side. Children render inside the flex row.
    render(_a, _ctx, childrenHtml = '') {
      return `
      <div style="margin-bottom: 32px;">
        <div style="display: flex; align-items: center; gap: 12px;">
          ${childrenHtml}
        </div>
      </div>`;
    },
    singleton: true,
    type: 'header',
  },

  heading: {
    category: 'content',
    controls: textControls(),
    defaultAttributes: {
      fontColor: '#000000',
      fontFamily: 'Arial, sans-serif',
      fontSize: 24,
      fontStyle: 'none',
      fontWeight: 'bold',
      letterSpacing: 0,
      lineHeight: 1,
      text: 'Heading',
    },
    displayName: 'Heading',
    icon: 'LineFont',
    paletteVisible: true,
    render(a) {
      return `<h1 style="${textBlockStyle(a, { margin: '0 0 16px 0', textAlign: 'left' })}">${a.text}</h1>`;
    },
    type: 'heading',
  },

  icon: {
    category: 'media',
    controls: [
      { attr: 'iconUrl', group: 'Content', kind: 'image', label: 'Icon', placeholder: 'Or paste a URL or inline SVG' },
    ],
    defaultAttributes: { iconUrl: '' },
    displayName: 'Icon',
    icon: 'LineShieldCheck',
    paletteVisible: true,
    render(a) {
      const iconUrl = String(a.iconUrl || '');

      return `
      <div style="display: block; margin-bottom: 16px;">
        ${iconUrl.startsWith('<') ? iconUrl : `<img src="${iconUrl}" alt="Icon" style="width: 56px; height: 57px; max-width: 100%; max-height: 100%; object-fit: contain" />`}
      </div>`;
    },
    singleton: true,
    type: 'icon',
  },

  image: {
    category: 'media',
    controls: [
      { attr: 'src', group: 'Content', kind: 'image', label: 'Image', placeholder: 'Or paste an image URL' },
      { attr: 'alt', group: 'Content', kind: 'text', label: 'Alt Text' },
      { attr: 'width', group: 'Style', kind: 'text', label: 'Width (e.g. 200px or auto)' },
    ],
    defaultAttributes: { alt: 'Image', src: '', width: 'auto' },
    displayName: 'Image',
    icon: 'LineImage',
    paletteVisible: true,
    render(a) {
      return `
      <div style="margin: 16px 0;">
        <img src="${a.src}" alt="${a.alt}" style="display: block; width: ${a.width}; max-width: 100%; height: auto; object-fit: contain;" />
      </div>`;
    },
    type: 'image',
  },

  logo: {
    category: 'media',
    controls: [
      { attr: 'logoUrl', group: 'Content', kind: 'image', label: 'Logo', placeholder: 'Or paste an image URL' },
      { attr: 'desktopLogoWidth', group: 'Style', kind: 'number', label: 'Max Width (px)' },
    ],
    defaultAttributes: { desktopLogoWidth: 75, logoUrl: '' },
    displayName: 'Logo',
    icon: 'LineImage',
    paletteVisible: true,
    // Image only — the brand-name text is its own `brandName` block, grouped beside this in a `header`.
    render(a, ctx) {
      const logoUrl = String(a.logoUrl || '');

      return `<span style="display: flex; align-items: center; max-width: ${a.desktopLogoWidth}px;">
        ${logoUrl ? `<img src="${logoUrl}" alt="${ctx.storeName}" style="display: block; width: auto; max-width: 100%; height: auto; object-fit: contain;" class="logo" />` : DEFAULT_LOGO_SVG}
      </span>`;
    },
    type: 'logo',
  },

  paragraph: {
    category: 'content',
    controls: textControls(),
    defaultAttributes: {
      fontColor: '#000000',
      fontFamily: 'Arial, sans-serif',
      fontSize: 24,
      fontStyle: 'none',
      fontWeight: 'regular',
      letterSpacing: 0,
      lineHeight: 1.5,
      text: 'Paragraph text',
    },
    displayName: 'Paragraph',
    icon: 'LineFont',
    paletteVisible: true,
    render(a) {
      return `<p style="${textBlockStyle(a, { margin: '0 0 16px 0', textAlign: 'left' })}">${a.text}</p>`;
    },
    type: 'paragraph',
  },

  reviews: {
    category: 'content',
    controls: [],
    defaultAttributes: {},
    displayName: 'Review Stars',
    icon: 'LineCheckCircle',
    paletteVisible: false,
    render(_a, ctx) {
      const open = (rating: number) => (!ctx.isPreview ? `<a href="[[reviewUrl]]?rating=${rating}">` : '');
      const close = !ctx.isPreview ? '</a>' : '';

      return `
    <div style="margin: 32px 0 0 0; text-align: left;">
      <p style="margin: 0 0 24px; line-height: 24px; color: rgba(29, 36, 51, 0.8); font-weight: bold;">
        How would you rate your experience with us?
      </p>
      <table align="center" style="margin-left: auto; margin-right: auto; width: 100%; justify-content: flex-start; background-color: #FFFFFF !important; background: #FFFFFF !important;" cellpadding="0" cellspacing="0" role="presentation">
        <tr><td style="padding-bottom: 8px; background-color: #FFFFFF !important; background: #FFFFFF !important;">
          ${open(5)}
          <img src="https://storage.googleapis.com/cdn.orderprotection.com/branding/email/5-5.png?v=1" style="border: 0; max-width: 100%; vertical-align: middle; line-height: 100%; width: 282px;" alt="5 stars">
          ${close}
        </td></tr>
        <tr><td style="padding-bottom: 8px; background-color: #FFFFFF !important; background: #FFFFFF !important;">
          ${open(4)}
          <img src="https://storage.googleapis.com/cdn.orderprotection.com/branding/email/4-5.png?v=1" style="border: 0; max-width: 100%; vertical-align: middle; line-height: 100%; width: 282px;" alt="4 stars">
          ${close}
        </td></tr>
        <tr><td style="padding-bottom: 8px; background-color: #FFFFFF !important; background: #FFFFFF !important;">
          ${open(3)}
          <img src="https://storage.googleapis.com/cdn.orderprotection.com/branding/email/3-5.png?v=1" style="border: 0; max-width: 100%; vertical-align: middle; line-height: 100%; width: 282px;" alt="3 stars">
          ${close}
        </td></tr>
        <tr><td style="padding-bottom: 8px; background-color: #FFFFFF !important; background: #FFFFFF !important;">
          ${open(2)}
          <img src="https://storage.googleapis.com/cdn.orderprotection.com/branding/email/2-5.png?v=1" style="border: 0; max-width: 100%; vertical-align: middle; line-height: 100%; width: 282px;" alt="2 stars">
          ${close}
        </td></tr>
        <tr><td style="padding-bottom: 8px; background-color: #FFFFFF !important; background: #FFFFFF !important;">
          ${open(1)}
          <img src="https://storage.googleapis.com/cdn.orderprotection.com/branding/email/1-5.png?v=1" style="border: 0; max-width: 100%; vertical-align: middle; line-height: 100%; width: 282px;" alt="1 star">
          ${close}
        </td></tr>
      </table>
    </div>`;
    },
    templateScoped: ['approved'],
    type: 'reviews',
  },

  spacer: {
    category: 'layout',
    controls: [{ attr: 'height', group: 'Spacing', kind: 'number', label: 'Height (px)' }],
    defaultAttributes: { height: 24 },
    displayName: 'Spacer',
    icon: 'LineBars',
    paletteVisible: true,
    render(a) {
      return `<div style="height: ${a.height}px; line-height: ${a.height}px; font-size: 0;">&nbsp;</div>`;
    },
    type: 'spacer',
  },

  storeCreditValue: {
    category: 'content',
    controls: [...buttonControls, { attr: 'fontFamily', group: 'Style', kind: 'font', label: 'Font' }],
    defaultAttributes: {
      background: '#000000',
      borderRadius: 0,
      fontColor: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'none',
      fontWeight: 'regular',
      link: '#',
      text: 'Start Shopping',
    },
    displayName: 'Store Credit Box',
    icon: 'LineUsdCircle',
    paletteVisible: false,
    render(a) {
      return `
  <div style="background:rgba(248, 245, 255, 0.93); border-radius: 12px; padding: 16px 5em; margin: 32px auto 0 auto; text-align: center;">
    <div style="font-size: 16px; color: #1D2433; margin-bottom: 8px;">Total Value</div>
    <div style="font-size: 40px; font-weight: bold; color: #000; margin-bottom: 16px;">[[totalValue]]</div>
    <div style="background: #fff; border-radius: 8px; padding: 16px; font-size: 20px; font-weight: 600; letter-spacing: 2px; margin-bottom: 12px; border: 1px solid #E1E6EF;">
      <div style="font-size: 14px; color: #A3A9B7; margin-bottom: 8px; font-weight: 400; font-family: ${a.fontFamily};">Your Code</div>
      [[code]]
    </div>
    <div style="font-size: 13px; color: #1D2433; margin-bottom: 16px;">
      Use this to redeem your store credit.<br>
      <span style="color: #5B1FD3; text-decoration: underline; cursor: pointer;">View breakdown of your reimbursement</span>
    </div>
        <a href="${safeHref(a.link)}" style="
          display: block;
          background-color: ${a.background};
          border-radius: ${a.borderRadius}px;
          color: ${a.fontColor};
          font-family: ${a.fontFamily};
          font-size: 16px;
          text-transform: ${a.fontStyle};
          font-weight: ${a.fontWeight};
          text-decoration: none;
          text-align: center;
          min-height: 58px;
          gap: 10px;
          margin: 48px auto 16px 0;
          align-content: center;
        ">${a.text}
        </a>
  </div>`;
    },
    templateScoped: ['storeCredit'],
    type: 'storeCreditValue',
  },

  subtext: {
    category: 'content',
    controls: textControls(),
    defaultAttributes: {
      fontColor: '#000000',
      fontFamily: 'Arial, sans-serif',
      fontSize: 20,
      fontStyle: 'none',
      fontWeight: 'regular',
      letterSpacing: 0,
      lineHeight: 1.5,
      text: 'Sub text',
    },
    displayName: 'Sub-text',
    icon: 'LineFont',
    paletteVisible: true,
    render(a) {
      return `<div style="${textBlockStyle(a, { margin: '0' })}">${a.text}</div>`;
    },
    type: 'subtext',
  },

  unreadMessage: {
    category: 'content',
    controls: [],
    defaultAttributes: {},
    displayName: 'Unread Message',
    icon: 'LineInbox',
    paletteVisible: false,
    render() {
      return `
    <div style="margin-bottom: 32px; width: 100%; border-radius: 24px; border-bottom-right-radius: 0; background-color: #f1f3f9; padding: 32px; box-sizing: border-box;">
      <p style="margin: 0; font-size: 16px; line-height: 24px; color: #1d2433;">
        Hello [[customerName]],<br /><br />
        [[message]]
      </p>
    </div>`;
    },
    templateScoped: ['unread'],
    type: 'unreadMessage',
  },

  unreadPostText: {
    category: 'content',
    controls: [],
    defaultAttributes: {},
    displayName: 'Unread Note',
    icon: 'LineFont',
    paletteVisible: false,
    render() {
      return `
    <p style="margin: 0 0 24px; font-size: 16px; line-height: 24px; color: rgba(29, 36, 51, 0.8);">
      If you haven't received a response within 24 hours, please don't hesitate to reply to this email.
    </p>`;
    },
    templateScoped: ['unread'],
    type: 'unreadPostText',
  },

  warrantyProducts: {
    category: 'content',
    controls: [],
    defaultAttributes: {},
    displayName: 'Warranty Products',
    icon: 'LineListUl',
    paletteVisible: false,
    render() {
      return `
    <div style="margin: 32px 0 0 0; text-align: left;">
      <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="border-collapse: collapse;">
        <tr style="border-bottom: 1px solid #E1E6EF;">
          <td style="padding: 16px 0; width: 64px; vertical-align: top;">
            <div style="width: 56px; height: 56px; background: #F1F3F9; border-radius: 8px;"></div>
          </td>
          <td style="padding: 16px 0 16px 12px; vertical-align: top;">
            <div style="font-weight: 600; font-size: 14px; color: #1D2433; margin-bottom: 4px;">Sample Product Name</div>
            <div style="font-size: 12px; color: #A3A9B7; margin-bottom: 2px;">SKU: SAMPLE-001</div>
            <div style="font-size: 12px; color: #A3A9B7; margin-bottom: 2px;">Color: Black &middot; Size: M</div>
            <div style="font-size: 12px; color: #A3A9B7;">Qty: 1</div>
          </td>
        </tr>
        <tr>
          <td style="padding: 16px 0; width: 64px; vertical-align: top;">
            <div style="width: 56px; height: 56px; background: #F1F3F9; border-radius: 8px;"></div>
          </td>
          <td style="padding: 16px 0 16px 12px; vertical-align: top;">
            <div style="font-weight: 600; font-size: 14px; color: #1D2433; margin-bottom: 4px;">Another Product Name</div>
            <div style="font-size: 12px; color: #A3A9B7; margin-bottom: 2px;">SKU: SAMPLE-002</div>
            <div style="font-size: 12px; color: #A3A9B7; margin-bottom: 2px;">Color: White &middot; Size: L</div>
            <div style="font-size: 12px; color: #A3A9B7;">Qty: 2</div>
          </td>
        </tr>
      </table>
    </div>`;
    },
    templateScoped: ['extendedWarranty'],
    type: 'warrantyProducts',
  },
};

export { FONT_OPTIONS };

export const EMAIL_BLOCK_TYPES = Object.keys(EMAIL_CATALOG) as EmailBlockSchema['type'][];
